import BrowserRouter from "@fuse/core/BrowserRouter";
import FuseAuthorization from "@fuse/core/FuseAuthorization/FuseAuthorization";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/store/hooks";
import FuseSplashScreen from "@fuse/core/FuseSplashScreen/FuseSplashScreen";
import { resetUser, selectUserRole, setUser } from "./user/store/userSlice";
import useAuth from "./useAuth";
import UserModel from "./user/models/UserModel";
import { User } from "./user";
import { useFCMService } from "../../services/fcmTokenService";

import { useAuthInfo, useRedirectFunctions } from "@propelauth/react";

type AuthenticationProps = {
  children: React.ReactNode;
};

function Authentication(props: AuthenticationProps) {
  const { children } = props;
  const { setAuthProvider, resetAuthProvider } = useAuth();

  // Get FCM service with axios instance
  const fcmTokenService = useFCMService();

  const userRole = useAppSelector(selectUserRole);
  const dispatch = useAppDispatch();

  /**
   * Auth Providers
   */

  /**
   * Custom Auth
   */

  const {
    user: propelUser,
    isLoggedIn: propelAuthStatus,
    loading: loading,
    userClass: userClass,
  } = useAuthInfo();

  const { redirectToAccountPage } = useRedirectFunctions();

  /**
   * isLoading
   */
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Check if services is in loading state
   */
  const inProgress = useMemo(() => loading, [loading]);

  /**
   * Any user is authenticated
   */
  const authenticated = useMemo(() => propelAuthStatus, [propelAuthStatus]);
  /**
   * All users are unauthenticated
   */
  const unAuthenticated = useMemo(() => !propelAuthStatus, [propelAuthStatus]);

  /**
   * Sign Out
   */
  useEffect(() => {
    if (!inProgress && unAuthenticated) {
      handleSignOut();
    }
  }, [inProgress, authenticated]);

  /**
   * Loading state is false when all services are done loading
   */
  useEffect(() => {
    if (!inProgress && !authenticated) {
      setIsLoading(false);
    }
  }, [inProgress, authenticated]);

  /**
   * Handle sign in
   */
  const handleSignIn = useCallback(
    (provider: string, userState: User) => {
      dispatch(setUser(userState)).then(() => {
        setAuthProvider(provider);
        setIsLoading(false);

        // Set up FCM token management after successful login
        fcmTokenService.setupFCMOnLogin().catch((error) => {
          console.error("Failed to set up FCM on login:", error);
        });
      });
    },
    [fcmTokenService]
  );

  /**
   * Handle sign out
   */
  const handleSignOut = useCallback(() => {
    // Clean up FCM service on logout
    fcmTokenService.cleanup();

    dispatch(resetUser());
    resetAuthProvider();
  }, [fcmTokenService]);

  /**
   * Handle Sign In on load
   */
  useEffect(() => {
    if (inProgress || !authenticated) {
      return;
    }

    if (propelUser) {
      const orgs = userClass.getOrgs();

      // Check if user has any organizations
      if (!orgs || orgs.length === 0) {
        // User has no organizations, they need to create one
        // Redirect to PropelAuth organization page
        redirectToAccountPage();
        return;
      }

      // Create org_id_to_org_member_info object
      const org_id_to_org_member_info = orgs.reduce((acc, org) => {
        acc[org.orgId] = {
          org_id: org.orgId,
          org_name: org.orgName,
          url_safe_org_name: org.urlSafeOrgName,
          org_metadata: org.orgMetadata || {},
          user_role: org.userAssignedRole,
          inherited_user_roles_plus_current_role:
            org.userInheritedRolesPlusCurrentRole || [],
          org_role_structure: org.orgRoleStructure,
          additional_roles: org.userAssignedAdditionalRoles || [],
          user_permissions: org.userPermissions || [],
        };
        return acc;
      }, {});

      const roles = orgs.map((org) => org.userAssignedRole);
      handleSignIn(
        "propel",
        UserModel({
          uid: propelUser.userId,
          data: {
            displayName: propelUser.username,
            email: propelUser.email,
          },
          role: roles,
          first_name: propelUser.firstName,
          last_name: propelUser.lastName,
          picture_url: propelUser.pictureUrl,
          org_id_to_org_member_info: org_id_to_org_member_info,
          properties: {
            chosen_font_size: propelUser.properties.chosen_font_size as string,
            chosen_language: propelUser.properties.chosen_language as string,
            phone_number: propelUser.properties.phone_number as string,
          },
        })
      );
    }
  }, [inProgress, authenticated, isLoading, propelUser]);

  return useMemo(
    () =>
      isLoading ? (
        <FuseSplashScreen />
      ) : (
        <BrowserRouter>
          <FuseAuthorization userRole={userRole}>{children}</FuseAuthorization>
        </BrowserRouter>
      ),
    [userRole, children, isLoading]
  );
}

export default Authentication;
